import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image, { ImageSchema } from '../../../components/Image';
import intl from '../../../locale';
import { ClickMarker, CommentClickMarker } from './ClickMarker';
import styles from './ImageClickMap.module.css';
import { CommentSchema, ImageClick } from './types';

//Координаты для смещения точки относительно области клика
//Необходимы для того, чтобы при клике со включенной опцией hasComment результатом клика был уголок курсора а не его центр
const CURSOR_CORNER_LEFT = 2;
const CURSOR_CORNER_TOP = 1;

export const calculateClickOffset = (
  event: React.MouseEvent<HTMLDivElement> | PointerEvent,
  top: number,
  left: number,
  height: number,
  width: number,
  hasComment: boolean,
): ImageClick => {
  const clientYOffset = ((event.clientY - top) * 100) / height;
  const clientXOffset = ((event.clientX - left) * 100) / width;

  const topOffset = hasComment ? clientYOffset + CURSOR_CORNER_TOP : clientYOffset;

  const leftOffset = hasComment ? clientXOffset - CURSOR_CORNER_LEFT : clientXOffset;

  return {
    top: topOffset.toFixed(3),
    left: leftOffset.toFixed(3),
    gridTop: topOffset.toFixed(0),
    gridLeft: leftOffset.toFixed(0),
  };
};

interface ImageClickMapProps extends CommentSchema {
  className?: string;
  image: ImageSchema;
  value: ImageClick[];
  readOnly?: boolean;
  children?: React.ReactNode;
  onChange: (clicks: ImageClick[]) => void;
  fullscreen?: boolean;
}

export const ImageClickMap: React.FC<ImageClickMapProps> = ({
  className,
  image,
  value: clicks,
  onChange,
  children,
  fullscreen = false,
  readOnly = false,
  hasComment = false,
  commentLimit = 255,
  commentLabel = intl.formatMessage({ id: 'System.CommentLabel' }),
}) => {
  const clickAreaRef = useRef<HTMLDivElement>(null);

  const [openedCommentIndex, setOpenedCommentIndex] = useState(-1);

  // "Временный" клик создается при добавлении клика с комментарием, при сохранении комментария "временный" клик становится обычным
  const [tempCommentClick, setTempCommentClick] = useState<ImageClick | null>(null);
  const tempClicks = useMemo(
    () => [...clicks, ...(tempCommentClick ? [tempCommentClick] : [])],
    [clicks, tempCommentClick],
  );
  const tempClickIndex = tempCommentClick ? tempClicks.length - 1 : -1;

  const deleteClick = (index: number): void => {
    if (index === tempClickIndex) {
      setTempCommentClick(null);
    } else {
      onChange(clicks.filter((_, i) => i !== index));
    }

    setOpenedCommentIndex(-1);
  };

  const handleAreaClick = (event: React.MouseEvent<HTMLDivElement> | PointerEvent) => {
    const area = clickAreaRef.current;

    if (readOnly || !area) {
      return;
    }

    const hasCommentText = hasComment && !!tempClicks[openedCommentIndex]?.comment;
    const hasOpenComment = openedCommentIndex !== -1;

    // При клике вне открытого комментария без текста необходимо закрыть его и удалить эту точку
    if (hasOpenComment && !hasCommentText) {
      setOpenedCommentIndex(-1);
      deleteClick(openedCommentIndex);
      return;
      // При клике вне открытого комментария c текстом необходимо сохранить его
    } else if (hasCommentText) {
      saveComment();
      return;
    }

    if (event.currentTarget !== area) {
      return;
    }

    const { top, left, height, width } = area.getBoundingClientRect();
    const click = calculateClickOffset(event, top, left, height, width, hasComment);

    if (hasComment) {
      setTempCommentClick(click);
      setOpenedCommentIndex(clicks.length);
    } else {
      onChange([...clicks, click]);
    }
  };

  const saveComment = () => {
    // При сохранении "временный" клик становится обычным
    if (tempCommentClick) {
      setTempCommentClick(null);
      onChange([...clicks, tempCommentClick]);
    }
    setOpenedCommentIndex(-1);
  };

  const changeComment = ({ comment, index }: { comment: string; index: number }) => {
    if (tempCommentClick) {
      setTempCommentClick({ ...tempCommentClick, comment });
    } else {
      onChange(clicks.map((click, i) => (i === index ? { ...click, comment } : click)));
    }
  };

  const openComment = (index: number) => {
    const previousOpenClick = tempClicks[openedCommentIndex];

    if (previousOpenClick && !previousOpenClick.comment) {
      deleteClick(openedCommentIndex);
    }

    setOpenedCommentIndex(index);
  };

  useEffect(() => {
    if (readOnly) {
      return;
    }

    window.addEventListener('pointerdown', handleAreaClick);
    return () => {
      window.removeEventListener('pointerdown', handleAreaClick);
    };
  }, [tempClicks, openedCommentIndex, readOnly]);

  return (
    <div className={cn(styles.root, className)}>
      <Image image={image} className={cn(styles.image, { [c(styles.fullscreen)]: fullscreen })} />

      <div
        ref={clickAreaRef}
        className={cn(styles.clickArea, { [c(styles.clickAreaComment)]: hasComment })}
        {...(!readOnly && { onClick: handleAreaClick })}
      >
        {tempClicks.map((click, index) => {
          const key = `${click.gridTop}:${click.gridLeft}`;

          const commonProps = {
            key,
            click: {
              ...click,
              ...(hasComment && {
                left: `${Number(click.left) + CURSOR_CORNER_LEFT}`,
                top: `${Number(click.top) - CURSOR_CORNER_TOP}`,
              }),
            },
            onDelete: () => {
              deleteClick(index);
            },
            readOnly,
            ariaLabel: `Комментарий ${index + 1}`,
          };

          return hasComment ? (
            <CommentClickMarker
              {...commonProps}
              active={index === openedCommentIndex}
              commentLimit={commentLimit}
              commentLabel={commentLabel}
              onSave={saveComment}
              onChange={comment => {
                changeComment({ comment, index });
              }}
              onClick={() => {
                openComment(index);
              }}
            />
          ) : (
            <ClickMarker {...commonProps} key={key} />
          );
        })}
      </div>

      {children}
    </div>
  );
};
